import React, { useEffect, useState, useCallback } from 'react';
import { lineTotal } from '../utils/money';
import { useParams, useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';
import ProductSearch from '../components/ProductSearch';
import CustomerSearch from '../components/CustomerSearch';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

const TABS = ['Sales', 'Purchases', 'Expenses', 'Assets'];

const EMPTY_SALE     = { product: null, buyer_name: '', buyer_contact: '', qty: '', unit_price: '', payment_status: 'paid', amount_paid: '', sku: '', reorder_level: '', expiry_date: '' };
const EMPTY_PURCHASE = { product: null, qty: '', unit_cost: '', supplier: '', sku: '', reorder_level: '' };
const EMPTY_EXPENSE  = { category: '', amount: '', description: '', supplier: '', expense_at: '' };
const EMPTY_ASSET    = { name: '', category: 'equipment', value: '', supplier: '', acquired_at: '', description: '' };

const KPI_TYPE = {
  'Revenue':      'revenue',
  'Gross Profit': 'gross-profit',
  'Expenses':     'expenses',
  'Net Profit':   'net-profit',
  'Receivables':  'receivables',
  'Stock Value':  'stock-value',
};

export default function BusinessPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState('Sales');

  const [showForm, setShowForm] = useState(false);
  const [saleForm, setSaleForm]         = useState(EMPTY_SALE);
  const [purchaseForm, setPurchaseForm] = useState(EMPTY_PURCHASE);
  const [expenseForm, setExpenseForm]   = useState(EMPTY_EXPENSE);
  const [assetForm, setAssetForm]       = useState(EMPTY_ASSET);
  const [saving, setSaving]   = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    companyAPI.getBusinessDetail(id)
      .then(r => setData(r.data))
      .catch((err) => setError(loadError(err, 'Failed to load business data')))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);


  const cancelForm = () => {
    setShowForm(false);
    setFormError('');
    setSaleForm(EMPTY_SALE);
    setPurchaseForm(EMPTY_PURCHASE);
    setExpenseForm(EMPTY_EXPENSE);
    setAssetForm(EMPTY_ASSET);
  };

  const handleTabChange = (t) => { setTab(t); cancelForm(); };

  const handleAddSale = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!saleForm.product) { setFormError('Please select or enter a product'); return; }
    if (saleForm.payment_status !== 'paid' && !saleForm.buyer_name.trim()) {
      setFormError('Buyer name is required for credit or partial sales');
      return;
    }
    setSaving(true);
    try {
      await companyAPI.addBizSale(id, {
        product_id:    saleForm.product.id   || undefined,
        product_name:  saleForm.product.id   ? undefined : saleForm.product.name,
        sku:           saleForm.product.id   ? undefined : saleForm.sku || undefined,
        reorder_level: saleForm.product.id   ? undefined : saleForm.reorder_level || undefined,
        buyer_name:    saleForm.buyer_name,
        buyer_contact: saleForm.buyer_contact || undefined,
        qty:           saleForm.qty,
        unit_price:    saleForm.unit_price,
        payment_status: saleForm.payment_status,
        amount_paid: saleForm.payment_status === 'paid'
          ? lineTotal(saleForm.qty, saleForm.unit_price)
          : saleForm.payment_status === 'partial' ? saleForm.amount_paid : 0,
        sold_at: new Date().toISOString(),
        expiry_date: saleForm.expiry_date || undefined,
      });
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add sale');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPurchase = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!purchaseForm.product) { setFormError('Please select or enter a product'); return; }
    setSaving(true);
    try {
      await companyAPI.addBizPurchase(id, {
        product_id:    purchaseForm.product.id   || undefined,
        product_name:  purchaseForm.product.id   ? undefined : purchaseForm.product.name,
        sku:           purchaseForm.product.id   ? undefined : purchaseForm.sku || undefined,
        reorder_level: purchaseForm.product.id   ? undefined : purchaseForm.reorder_level || undefined,
        qty:           purchaseForm.qty,
        unit_cost:     purchaseForm.unit_cost,
        supplier:      purchaseForm.supplier,
      });
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add purchase');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      await companyAPI.addBizAsset(id, assetForm);
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add asset');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      await companyAPI.addBizExpense(id, expenseForm);
      cancelForm();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><BackButton /><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><BackButton /><PageError error={error} /></div>;

  const { business, summary, sales, purchases, expenses, products, assets = [], customers = [] } = data;
  const cur = business.currency;

  const kpis = [
    { label: 'Revenue',      value: summary.revenue,      color: null },
    { label: 'Gross Profit', value: summary.gross_profit, color: summary.gross_profit >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
    { label: 'Expenses',     value: summary.expenses,     color: 'var(--color-danger)' },
    { label: 'Net Profit',   value: summary.net_profit,   color: summary.net_profit >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
    { label: 'Receivables',  value: summary.receivables,  color: 'var(--color-warning)' },
    { label: 'Stock Value',  value: summary.stock_value,  color: null },
  ];

  const records = { Sales: sales, Purchases: purchases, Expenses: expenses, Assets: assets };

  const renderRecord = (r) => {
    const openProduct = (from) => r.product_id
      ? { onClick: () => navigate(`/company/business/${id}/product/${r.product_id}?from=${from}`), style: { marginBottom: '0.6rem', cursor: 'pointer' } }
      : { style: { marginBottom: '0.6rem' } };
    if (tab === 'Sales') return (
      <div key={r.product_id} className="card" {...openProduct('sales')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.product_name || '—'}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '0.15rem' }}>
              {Number(r.transaction_count)} {Number(r.transaction_count) === 1 ? 'sale' : 'sales'} · qty {Number(r.total_qty).toFixed(2)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{cur} {fmt(r.total_revenue)}</span>
            {Number(r.outstanding) > 0 && (
              <span style={{ fontSize: '0.72rem', color: 'var(--color-warning)' }}>
                {cur} {fmt(r.outstanding)} outstanding
              </span>
            )}
          </div>
        </div>
      </div>
    );
    if (tab === 'Purchases') return (
      <div key={r.id} className="card" {...openProduct('purchases')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.product_name || '—'}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '0.15rem' }}>
              Qty {Number(r.qty).toFixed(2)} · {fmtDate(r.purchased_at)}{r.supplier ? ` · ${r.supplier}` : ''}
            </div>
          </div>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{cur} {fmt(r.total_cost)}</span>
        </div>
      </div>
    );
    if (tab === 'Expenses') return (
      <div key={r.id} className="card" style={{ marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'capitalize' }}>{r.category}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '0.15rem' }}>
              {fmtDate(r.expense_at)}
              {r.supplier ? ` · ${r.supplier}` : ''}
              {r.description ? ` · ${r.description}` : ''}
            </div>
          </div>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-danger)' }}>{cur} {fmt(r.amount)}</span>
        </div>
      </div>
    );
    if (tab === 'Assets') return (
      <div key={r.id} className="card" style={{ marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.name}</div>
            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: '0.15rem' }}>
              <span style={{ textTransform: 'capitalize' }}>{r.category}</span>
              {` · ${fmtDate(r.acquired_at)}`}
              {r.supplier ? ` · ${r.supplier}` : ''}
              {r.description ? ` · ${r.description}` : ''}
            </div>
          </div>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{cur} {fmt(r.value)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">{business.name}</h1>
      <p className="text-muted" style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>{cur}</p>

      {/* KPI summary */}
      <div className="kpi-grid" style={{ marginBottom: '1.25rem' }}>
        {kpis.map(k => (
          <div
            key={k.label}
            className="card kpi-card"
            onClick={() => navigate(`/company/business/${id}/kpi/${KPI_TYPE[k.label]}`)}
            style={{ cursor: 'pointer' }}
          >
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value" style={{ color: k.color || 'inherit' }}>{cur} {fmt(k.value)}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => handleTabChange(t)}
            style={{
              flex: 1, padding: '0.5rem', borderRadius: 'var(--radius-sm)', border: 'none',
              background: tab === t ? 'var(--color-primary)' : 'var(--color-surface)',
              color: tab === t ? '#fff' : 'var(--color-muted)',
              fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>Recent {tab.toLowerCase()}</span>
        <button
          onClick={() => { setShowForm(s => !s); setFormError(''); }}
          style={{
            padding: '0.35rem 0.85rem',
            fontSize: '0.8rem',
            fontWeight: 600,
            background: showForm ? 'transparent' : 'var(--color-primary)',
            color: showForm ? 'var(--color-muted)' : '#fff',
            border: showForm ? '1px solid var(--color-border)' : 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {showForm ? 'Cancel' : `+ Add ${tab.slice(0, -1)}`}
        </button>
      </div>

      {/* Add forms */}
      {showForm && tab === 'Sales' && (
        <form onSubmit={handleAddSale} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <ProductSearch
            products={products}
            value={saleForm.product}
            onChange={p => setSaleForm(f => ({
              ...f,
              product: p,
              unit_price: p?.sale_price != null && Number(p.sale_price) > 0
                ? String(Number(p.sale_price))
                : f.unit_price,
            }))}
            placeholder="Search or enter product name"
          />
          {saleForm.product && !saleForm.product.id && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input type="text" placeholder="SKU (optional)" value={saleForm.sku} onChange={e => setSaleForm(f => ({ ...f, sku: e.target.value }))} style={{ flex: 1 }} />
              <input type="text" inputMode="decimal" placeholder="Min stock" value={saleForm.reorder_level} onChange={e => setSaleForm(f => ({ ...f, reorder_level: e.target.value }))} style={{ flex: 1 }} />
            </div>
          )}
          <CustomerSearch
            customers={customers}
            value={saleForm.buyer_name}
            onChange={name => setSaleForm(f => ({ ...f, buyer_name: name }))}
            onSelect={c => setSaleForm(f => ({ ...f, buyer_name: c.name, buyer_contact: c.contact || f.buyer_contact }))}
            placeholder={saleForm.payment_status === 'paid' ? 'Buyer name (optional)' : 'Buyer name'}
            required={saleForm.payment_status !== 'paid'}
          />
          <input type="text" placeholder="Contact / phone (optional)" value={saleForm.buyer_contact} onChange={e => setSaleForm(f => ({ ...f, buyer_contact: e.target.value }))} style={{ width: '100%' }} />
          <MoneyInput placeholder="Quantity" value={saleForm.qty} onChange={e => setSaleForm(f => ({ ...f, qty: e.target.value }))} required style={{ width: '100%' }} />
          <MoneyInput placeholder={`Unit price (${cur})`} value={saleForm.unit_price} onChange={e => setSaleForm(f => ({ ...f, unit_price: e.target.value }))} required style={{ width: '100%' }} />
          {saleForm.product?.is_service && (
            <div>
              <label style={{ fontSize: '0.78rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>Subscription expiry date</label>
              <input type="date" value={saleForm.expiry_date} onChange={e => setSaleForm(f => ({ ...f, expiry_date: e.target.value }))} style={{ width: '100%' }} />
            </div>
          )}
          <select value={saleForm.payment_status} onChange={e => setSaleForm(f => ({ ...f, payment_status: e.target.value }))} style={{ width: '100%' }}>
            <option value="paid">Paid in full</option>
            <option value="partial">Partial payment</option>
            <option value="debt">On credit (debt)</option>
          </select>
          {saleForm.payment_status === 'partial' && (
            <MoneyInput placeholder={`Amount paid (${cur})`} value={saleForm.amount_paid} onChange={e => setSaleForm(f => ({ ...f, amount_paid: e.target.value }))} style={{ width: '100%' }} />
          )}
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record Sale'}</button>
        </form>
      )}

      {showForm && tab === 'Purchases' && (
        <form onSubmit={handleAddPurchase} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <ProductSearch
            products={products}
            value={purchaseForm.product}
            onChange={p => setPurchaseForm(f => ({ ...f, product: p }))}
            placeholder="Search or enter product name"
          />
          {purchaseForm.product && !purchaseForm.product.id && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input type="text" placeholder="SKU (optional)" value={purchaseForm.sku} onChange={e => setPurchaseForm(f => ({ ...f, sku: e.target.value }))} style={{ flex: 1 }} />
              <input type="text" inputMode="decimal" placeholder="Min stock" value={purchaseForm.reorder_level} onChange={e => setPurchaseForm(f => ({ ...f, reorder_level: e.target.value }))} style={{ flex: 1 }} />
            </div>
          )}
          <MoneyInput placeholder="Quantity" value={purchaseForm.qty} onChange={e => setPurchaseForm(f => ({ ...f, qty: e.target.value }))} required style={{ width: '100%' }} />
          <MoneyInput placeholder={`Unit cost (${cur})`} value={purchaseForm.unit_cost} onChange={e => setPurchaseForm(f => ({ ...f, unit_cost: e.target.value }))} required style={{ width: '100%' }} />
          <input type="text" placeholder="Supplier (optional)" value={purchaseForm.supplier} onChange={e => setPurchaseForm(f => ({ ...f, supplier: e.target.value }))} style={{ width: '100%' }} />
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record Purchase'}</button>
        </form>
      )}

      {showForm && tab === 'Expenses' && (
        <form onSubmit={handleAddExpense} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <input type="text" placeholder="Category (e.g. Rent, Utilities)" value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))} required style={{ width: '100%' }} />
          <MoneyInput placeholder={`Amount (${cur})`} value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} required style={{ width: '100%' }} />
          <input type="text" placeholder="Supplier / payee (optional)" value={expenseForm.supplier} onChange={e => setExpenseForm(f => ({ ...f, supplier: e.target.value }))} style={{ width: '100%' }} />
          <input type="text" placeholder="Description (optional)" value={expenseForm.description} onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))} style={{ width: '100%' }} />
          <div>
            <label style={{ fontSize: '0.78rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>Date</label>
            <input type="date" value={expenseForm.expense_at} onChange={e => setExpenseForm(f => ({ ...f, expense_at: e.target.value }))} style={{ width: '100%' }} />
          </div>
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record Expense'}</button>
        </form>
      )}

      {showForm && tab === 'Assets' && (
        <form onSubmit={handleAddAsset} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          <input type="text" placeholder="Asset name (e.g. Delivery Van, Laptop)" value={assetForm.name} onChange={e => setAssetForm(f => ({ ...f, name: e.target.value }))} required style={{ width: '100%' }} />
          <select value={assetForm.category} onChange={e => setAssetForm(f => ({ ...f, category: e.target.value }))} style={{ width: '100%' }}>
            <option value="equipment">Equipment</option>
            <option value="vehicle">Vehicle</option>
            <option value="furniture">Furniture</option>
            <option value="land">Land / Property</option>
            <option value="technology">Technology</option>
            <option value="other">Other</option>
          </select>
          <MoneyInput placeholder={`Value / cost (${cur})`} value={assetForm.value} onChange={e => setAssetForm(f => ({ ...f, value: e.target.value }))} required style={{ width: '100%' }} />
          <input type="text" placeholder="Supplier / vendor (optional)" value={assetForm.supplier} onChange={e => setAssetForm(f => ({ ...f, supplier: e.target.value }))} style={{ width: '100%' }} />
          <div>
            <label style={{ fontSize: '0.78rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.25rem' }}>Date acquired</label>
            <input type="date" value={assetForm.acquired_at} onChange={e => setAssetForm(f => ({ ...f, acquired_at: e.target.value }))} style={{ width: '100%' }} />
          </div>
          <input type="text" placeholder="Description (optional)" value={assetForm.description} onChange={e => setAssetForm(f => ({ ...f, description: e.target.value }))} style={{ width: '100%' }} />
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Record Asset'}</button>
        </form>
      )}

      {/* Records list */}
      {records[tab].length === 0 && <p className="text-muted" style={{ fontSize: '0.9rem' }}>No {tab.toLowerCase()} yet.</p>}
      {records[tab].map(r => renderRecord(r))}
    </div>
  );
}
